import { Eye, EyeOff, GitFork, Layers3, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createCustomEnvironment,
  deleteCustomEnvironment,
  listCustomEnvironments,
  type CustomEnvironmentRow,
} from '../api';
import { EmptyState } from '../components/EmptyState';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};

function messageFrom(error: unknown, fallback: string) {
  const failure = error as RequestError;
  return failure?.response?.data?.message || failure?.message || fallback;
}

function EnvironmentsPage() {
  const { session } = useSession();
  const { confirm, toast } = useFeedback();
  const {
    workspace,
    activeOrganization,
    activeProject,
    isEnvironmentVisible,
    setEnvironmentVisible,
  } = useWorkspace();

  const { environments, runtimeConfigs, channels } = workspace;
  const [customEnvironments, setCustomEnvironments] = useState<CustomEnvironmentRow[]>([]);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [customError, setCustomError] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [forkedFromSlug, setForkedFromSlug] = useState('dev');

  const loadCustomEnvironments = useCallback(async () => {
    if (!session || !activeOrganization) return;
    setLoadingCustom(true);
    setCustomError('');
    try {
      setCustomEnvironments(
        await listCustomEnvironments(session.accessToken, {
          organizationId: activeOrganization.id,
          projectId: activeProject?.id,
        })
      );
    } catch (error: unknown) {
      setCustomError(messageFrom(error, 'Custom environments could not be loaded.'));
    } finally {
      setLoadingCustom(false);
    }
  }, [activeOrganization, activeProject?.id, session]);

  useEffect(() => {
    void loadCustomEnvironments();
  }, [loadCustomEnvironments]);

  const environmentRows = useMemo(
    () =>
      environments.map((environment) => {
        const environmentRuntimeConfigs = runtimeConfigs.filter(
          (config) => config.environment === environment.slug
        );
        const environmentChannels = channels.filter(
          (channel) => channel.environment === environment.slug
        );

        return {
          ...environment,
          visible: isEnvironmentVisible(environment.slug),
          runtimeCount: environmentRuntimeConfigs.length,
          channelCount: environmentChannels.length,
          servingCount: environmentChannels.filter(
            (channel) => channel.isActive && Boolean(channel.releaseArtifactId)
          ).length,
        };
      }),
    [channels, environments, isEnvironmentVisible, runtimeConfigs]
  );

  const visibleCount = environmentRows.filter((row) => row.visible).length;
  const servingCount = environmentRows.reduce((count, row) => count + row.servingCount, 0);

  async function handleCreate() {
    if (!session || !activeOrganization || creating) return;
    const nextSlug = slug.trim().toLowerCase();
    const nextLabel = label.trim();
    if (!nextSlug || !nextLabel) return;

    setCreating(true);
    try {
      await createCustomEnvironment(session.accessToken, {
        organizationId: activeOrganization.id,
        projectId: activeProject?.id,
        slug: nextSlug,
        label: nextLabel,
        forkedFromSlug,
      });
      setSlug('');
      setLabel('');
      setForkedFromSlug('dev');
      setShowCreate(false);
      toast('success', `${nextLabel} is ready for project configuration`);
      await loadCustomEnvironments();
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Unable to create the environment.'));
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(environment: CustomEnvironmentRow) {
    if (!session || !activeOrganization || deletingId) return;
    const accepted = await confirm({
      title: `Remove ${environment.label}?`,
      message:
        'This removes the environment from future configuration. Existing release history is retained, but anything still targeting this name should be moved first.',
      confirmLabel: 'Remove environment',
      danger: true,
    });
    if (!accepted) return;

    setDeletingId(environment.environmentId);
    try {
      await deleteCustomEnvironment(
        session.accessToken,
        environment.environmentId,
        activeOrganization.id
      );
      toast('success', `${environment.label} removed`);
      await loadCustomEnvironments();
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Unable to remove the environment.'));
    } finally {
      setDeletingId('');
    }
  }

  return (
    <div className="configurationPage">
      <div className="configurationSummary" aria-label="Environment summary">
        <div>
          <span>Visible</span>
          <strong>{visibleCount} / {environmentRows.length}</strong>
          <small>Shown in project controls</small>
        </div>
        <div>
          <span>Serving now</span>
          <strong>{servingCount}</strong>
          <small>Targets with a live bundle</small>
        </div>
        <div>
          <span>Development forks</span>
          <strong>{customEnvironments.length}</strong>
          <small>Isolated environment names</small>
        </div>
      </div>

      <section className="configurationSection" aria-labelledby="environment-catalog-title">
        <header className="configurationSectionHeader">
          <div>
            <div className="panelKicker">Project environments</div>
            <h2 id="environment-catalog-title">Available environments</h2>
            <p>Control which shared environments appear in release and runtime workflows.</p>
          </div>
        </header>

        {environmentRows.length > 0 ? (
          <div className="releaseTableWrap environmentTableWrap" role="region" aria-label="Available environments" tabIndex={0}>
            <table className="releaseTable environmentVisibilityTable">
              <thead>
                <tr>
                  <th>Environment</th>
                  <th>Runtime rules</th>
                  <th>Release targets</th>
                  <th>Serving now</th>
                  <th>Visibility</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {environmentRows.map((row) => (
                  <tr key={row.tenantEnvironmentId} className={row.visible ? undefined : 'isEnvironmentHidden'}>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{row.name}</strong>
                        <span>{row.region?.toUpperCase() || 'GLOBAL'} · {row.slug}</span>
                      </div>
                    </td>
                    <td>{row.runtimeCount}</td>
                    <td>{row.channelCount}</td>
                    <td>{row.servingCount}</td>
                    <td>
                      <span className={`visibilityState ${row.visible ? 'isVisible' : ''}`}>
                        {row.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                        {row.visible ? 'Shown' : 'Hidden'}
                      </span>
                    </td>
                    <td className="releaseTableAction">
                      <button
                        type="button"
                        className="secondaryButton"
                        aria-pressed={row.visible}
                        aria-label={`${row.visible ? 'Hide' : 'Show'} ${row.name} environment`}
                        onClick={() => setEnvironmentVisible(row.slug, !row.visible)}
                      >
                        {row.visible ? <EyeOff size={14} /> : <Eye size={14} />}
                        {row.visible ? 'Hide' : 'Show'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<Layers3 size={20} />}
            title="Environments are not available"
            description="The project environment catalog could not be loaded."
          />
        )}
        <p className="environmentVisibilityNote">
          Visibility is a local display preference. It never pauses delivery or deletes data.
        </p>
      </section>

      <section className="configurationSection" aria-labelledby="environment-forks-title">
        <header className="configurationSectionHeader">
          <div>
            <div className="panelKicker">Development isolation</div>
            <h2 id="environment-forks-title">Development forks</h2>
            <p>Create an environment name rooted in dev for release, domain, and runtime configuration.</p>
          </div>
          <button type="button" className="panelControl" onClick={() => setShowCreate((open) => !open)}>
            <Plus size={14} />
            {showCreate ? 'Cancel' : 'New fork'}
          </button>
        </header>

        <div className="configurationCallout">
          A fork creates an isolated configuration target; it does not copy live bundles or infrastructure. Only Development or another development fork can be used as a source.
        </div>

        {showCreate ? (
          <div className="configurationInlineForm" aria-label="Create development fork">
            <label>
              <span>Name</span>
              <input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Jamie review" />
            </label>
            <label>
              <span>Slug</span>
              <input value={slug} onChange={(event) => setSlug(event.target.value)} placeholder="jamie-review" />
            </label>
            <label>
              <span>Based on</span>
              <select value={forkedFromSlug} onChange={(event) => setForkedFromSlug(event.target.value)}>
                <option value="dev">Development</option>
                {customEnvironments.map((environment) => (
                  <option key={environment.environmentId} value={environment.slug}>{environment.label}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="primaryButton"
              disabled={creating || !label.trim() || !slug.trim()}
              onClick={handleCreate}
            >
              <GitFork size={14} />
              {creating ? 'Creating…' : 'Create fork'}
            </button>
          </div>
        ) : null}

        {customError ? (
          <div className="configurationError" role="alert">
            <span>{customError}</span>
            <button type="button" className="secondaryButton" onClick={() => void loadCustomEnvironments()}>Retry</button>
          </div>
        ) : customEnvironments.length > 0 ? (
          <div className="configurationList" aria-label="Development forks">
            {customEnvironments.map((environment) => (
              <div className="configurationListRow" key={environment.environmentId}>
                <span className="serviceMark"><GitFork size={14} /></span>
                <div className="configurationListPrimary">
                  <strong>{environment.label}</strong>
                  <span>{environment.slug}</span>
                </div>
                <div className="configurationListMeta">
                  <span>Based on</span>
                  <strong>{environment.forkedFromSlug}</strong>
                </div>
                <button
                  type="button"
                  className="secondaryButton dangerQuietButton"
                  disabled={deletingId === environment.environmentId}
                  onClick={() => void handleDelete(environment)}
                >
                  <Trash2 size={13} />
                  {deletingId === environment.environmentId ? 'Removing…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        ) : !loadingCustom ? (
          <EmptyState
            icon={<GitFork size={20} />}
            title="No development forks"
            description="Create one when a developer needs an isolated release or domain configuration target."
          />
        ) : (
          <div className="configurationLoading">Loading development forks…</div>
        )}
      </section>
    </div>
  );
}

export { EnvironmentsPage };
